import { useRef, useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { makeStyles } from '@material-ui/core/styles'

import UserAvatar from '../chat/user-avatar'

type UserProfileCardProps = {
  userName?: string | null
  userEmail?: string | null
  tenantName?: string | null
  onSignOut?: () => void
}

const useStyles = makeStyles(() => ({
  root: {
    position: 'relative',
  },
  trigger: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '14px',
    background: 'transparent',
    border: 0,
    cursor: 'pointer',
    transition: 'background-color 200ms ease',
    '&:hover': {
      backgroundColor: 'var(--ds-bg-subtle-hover, #e7eaf1)',
    },
  },
  triggerOpen: {
    backgroundColor: 'var(--ds-bg-subtle-hover, #e7eaf1)',
  },
  textBlock: {
    flex: 1,
    minWidth: 0,
    textAlign: 'left',
  },
  name: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--ds-fg-primary, #1f2430)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    margin: 0,
  },
  email: {
    fontSize: '0.75rem',
    color: 'var(--ds-fg-tertiary, #8c92a5)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    margin: 0,
  },
  chevron: {
    width: 16,
    height: 16,
    flexShrink: 0,
    color: 'var(--ds-fg-tertiary, #8c92a5)',
    transition: 'transform 200ms ease',
  },
  chevronOpen: {
    transform: 'rotate(180deg)',
  },
  dropdown: {
    position: 'fixed',
    width: 288,
    backgroundColor: 'var(--ds-bg-elevated, #ffffff)',
    border: '1px solid var(--ds-border-default, #d7dae4)',
    borderRadius: 12,
    boxShadow: '0 18px 40px rgba(15,23,42,0.18)',
    backdropFilter: 'blur(4px)',
    overflow: 'hidden',
    zIndex: 1400,
  },
  userCard: {
    padding: '24px',
    borderBottom: '1px solid var(--ds-border-default, #d7dae4)',
    backgroundColor: 'var(--ds-bg-subtle, #f1f2f7)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
  },
  userCardName: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: 'var(--ds-fg-primary, #1f2430)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    margin: 0,
  },
  userCardEmail: {
    fontSize: '0.875rem',
    color: 'var(--ds-fg-secondary, #535b6c)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    marginTop: 4,
    marginBottom: 0,
  },
  tenantRow: {
    padding: '12px 16px',
    borderBottom: '1px solid var(--ds-border-default, #d7dae4)',
  },
  tenantLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--ds-fg-tertiary, #8c92a5)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 4,
  },
  tenantName: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--ds-fg-primary, #1f2430)',
  },
  actions: {
    padding: 12,
  },
  signOutButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '10px 16px',
    borderRadius: 8,
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--ds-fg-secondary, #535b6c)',
    background: 'transparent',
    border: 0,
    cursor: 'pointer',
    transition: 'background-color 150ms ease, color 150ms ease',
    '&:hover': {
      backgroundColor: 'var(--ds-bg-surface-hover, rgba(241,242,247,0.45))',
      color: 'var(--ds-fg-primary, #1f2430)',
    },
  },
}))

const UserProfileCard = ({ userName, userEmail, tenantName, onSignOut }: UserProfileCardProps) => {
  const classes = useStyles()
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const [dropdownStyle, setDropdownStyle] = useState<{ bottom: string; left: string }>({
    bottom: '0px',
    left: '0px',
  })

  const toggleOpen = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setDropdownStyle({
        bottom: `${window.innerHeight - rect.top + 8}px`,
        left: `${rect.left}px`,
      })
    }
    setIsOpen((prev) => !prev)
  }

  const handleSignOut = () => {
    setIsOpen(false)
    onSignOut?.()
  }

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setIsOpen(false)
    }
  }, [])

  useEffect(() => {
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [handleClickOutside])

  const displayName = userName || userEmail || 'User'

  return (
    <div ref={containerRef} className={classes.root}>
      <button
        ref={triggerRef}
        type="button"
        className={`${classes.trigger} ${isOpen ? classes.triggerOpen : ''}`}
        onClick={toggleOpen}
      >
        <div style={{ width: 32, height: 32, flexShrink: 0 }}><UserAvatar type="user" /></div>
        <div className={classes.textBlock}>
          <p className={classes.name}>{displayName}</p>
          {userEmail && <p className={classes.email}>{userEmail}</p>}
        </div>
        <svg
          className={`${classes.chevron} ${isOpen ? classes.chevronOpen : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && createPortal(
        <div className={classes.dropdown} style={dropdownStyle}>
          <div className={classes.userCard}>
            <UserAvatar type="user" />
            <div style={{ textAlign: 'center', width: '100%' }}>
              <p className={classes.userCardName}>{userName || 'User'}</p>
              {userEmail && <p className={classes.userCardEmail}>{userEmail}</p>}
            </div>
          </div>

          {tenantName && (
            <div className={classes.tenantRow}>
              <div className={classes.tenantLabel}>Organization</div>
              <div className={classes.tenantName}>{tenantName}</div>
            </div>
          )}

          <div className={classes.actions}>
            <button
              type="button"
              className={classes.signOutButton}
              onClick={handleSignOut}
            >
              <svg width={16} height={16} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              Sign Out
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default UserProfileCard
